'use client';

/**
 * Fetch wrapper for Profileia API client.
 * Verbatim extraction from frontend/src/lib/api-client.ts:
 *   - API_PREFIX (L1230-1249)
 *   - timeout constants + FetchOptions + fetchWithTimeout (L1385-1564)
 *   - apiFetch (L1565-1710)
 *   - handleResponse (L1711-1841)
 *   - apiFetchValidated + ValidatableSchema (L1842-1896)
 *   - apiFetchFormData (L1897-1969)
 *   - refresh state + tryRefresh (L2009-2104)
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 */

import { ApiClientError } from './error-handler';
import { tokenStore } from './token-store';
import type { ApiError, ZodType } from './shared-types';
import { SERVER_ERROR_MESSAGES } from './error-handler';

// SPEC-050 Bug 3: include NEXT_PUBLIC_API_URL as the second fallback so the
// browser-side fetch resolves correctly on staging/prod where
// docker-compose sets NEXT_PUBLIC_API_URL (not NEXT_PUBLIC_API_PREFIX). The
// relative `/api` default still applies in dev (Next.js rewrite proxies
// /api/* → http://localhost:4000/api/*) and is preserved as the final
// fallback so the browser never fetches an absolute cross-origin URL when
// the deployer hasn't set the env.
const API_PREFIX =
  process.env.NEXT_PUBLIC_API_PREFIX ??
  process.env.NEXT_PUBLIC_API_URL ??
  '/api';
// AUDIT3-SHARED-007 (SPEC-046 QA AUDIT R1 IA — BATCH-6): removed the
// vestigial `ACCESS_KEY` constant + `void ACCESS_KEY;` dead-code marker
// (kept the JSDoc above for the historical "window.__profileia_access"
// context — the storage pattern is unchanged, the unused constant was
// leftover from an old tokenStore.set(access, refresh) backward-compat
// shim that has since been removed).

// ─── Token storage (solo access token en memoria) ───────────────────────────


// Backward-compat: previously the legacy `void ACCESS_KEY;` marker lived
// here. It was removed by AUDIT3-SHARED-007 — the constant was vestigial.

// ─── Fetch timeout helper ──────────────────────────────────────────────────

/**
 * Default request timeout. If a fetch takes longer than this, the underlying
 * AbortController fires and `apiFetch` throws an `ApiClientError` with
 * `code='TIMEOUT'`, `status=408` (retryable).
 *
 * Spec ref: BUG-EXP5-003 (SPEC-041 QA audit pre-launch) — previously a hung
 * backend or slow network left the UI in an infinite "loading" state with
 * no feedback. 30s is the OWASP-recommended default for interactive admin
 * endpoints (long enough for legitimate slow queries, short enough to surface
 * a stale connection).
 *
 * Callers MAY override per-call via `opts.timeoutMs` (e.g., the CSV export
 * streaming endpoint may pass `timeoutMs: 120_000` for large exports).
 *
 * Bug 2 (SPEC-049): the previous 30s single `DEFAULT_TIMEOUT_MS` made every
 * GET, POST, PUT, DELETE equally slow to fail. Split into three classes so:
 *   - READ_TIMEOUT_MS (8s) — used for GET requests (the slow-navigation case
 *     the founder reported). < 1s on healthy backend, < 8s on cold restart.
 *   - WRITE_TIMEOUT_MS (15s) — POST/PUT/DELETE — financial operations need
 *     more headroom for distributed locks + DB tx (Ledger, idempotency keys).
 *   - UPLOAD_TIMEOUT_MS (30s) — FormData (avatar upload, KYC docs upload) —
 *     large bodies + slow upstream (Hetzner shared bandwidth).
 */
const READ_TIMEOUT_MS = 8_000;
const WRITE_TIMEOUT_MS = 15_000;
// Sprint 139 fix (2026-09-23): increased from 30s to 120s. 30s was
// too short for image uploads on slow connections (3 × 5MB = 15MB
// at 1 Mbps = 120s). The admin edit dialog + seller product uploader
// both use apiFetchFormData for image uploads — 120s gives enough
// headroom for slow mobile connections + backend processing.
const UPLOAD_TIMEOUT_MS = 120_000;
/**
 * @deprecated Use READ_TIMEOUT_MS / WRITE_TIMEOUT_MS / UPLOAD_TIMEOUT_MS
 * (Bug 2 SPEC-049). Kept as a stable alias for callers that don't care
 * about the method-aware timeout — the value (30s) matches the pre-Bug-2
 * behavior so existing `opts.timeoutMs ?? DEFAULT_TIMEOUT_MS` overrides
 * don't change shape. Declared as the literal `30_000` (not
 * `UPLOAD_TIMEOUT_MS`) so static-source test assertions
 * (`bug-exp5-003-api-fetch-timeout.test.ts:35`) that check the source
 * text against the 30s literal keep matching — the runtime value is
 * identical either way.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Options for `apiFetch` / `apiFetchFormData`.
 *
 * AUDIT3-SHARED-008 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the `body` field
 * is RE-USED on the 401-retry path — `apiFetch` calls `fetchWithTimeout`
 * once, and if the response is 401, it calls `fetchWithTimeout` AGAIN with
 * the SAME `init` object (including `body`). The browser's `fetch()` reads
 * the body stream once; for bodies that are consumed destructively
 * (e.g., `ReadableStream`, `Blob`, `ArrayBuffer`), the second `fetch()`
 * call would fail with a TypeError (`body has already been used`).
 *
 * CONSTRAINT: callers MUST pass a `body` that is re-usable across two
 * `fetch()` invocations. Safe body types:
 *   - `string` (e.g., `JSON.stringify(payload)`) — recommended for JSON APIs
 *   - `FormData` — recommended for multipart uploads (immutable per spec)
 *   - `URLSearchParams` — recommended for form-encoded bodies
 *
 * Unsafe body types (will throw on 401-retry):
 *   - `Blob` (one-shot read)
 *   - `ArrayBuffer` / `TypedArray` (one-shot read)
 *   - `ReadableStream` (one-shot read)
 *
 * If you MUST send a one-shot body type, set `skipRefresh: true` so the
 * 401-retry path is bypassed and the body is consumed only once.
 */
interface FetchOptions extends RequestInit {
  auth?: boolean;
  skipRefresh?: boolean;
  /**
   * Per-request timeout in milliseconds. Defaults to a method-aware value
   * (Bug 2 SPEC-049): 8s for GET, 15s for POST/PUT/DELETE without a
   * FormData body, 30s for FormData uploads. When the timeout elapses,
   * `apiFetch` aborts the underlying fetch and throws
   * `ApiClientError(code='TIMEOUT', status=408)`. Pass `0` to disable the
   * timeout entirely (use sparingly — only for streaming downloads where
   * you manage progress separately).
   */
  timeoutMs?: number;
  /**
   * Bug 6 (SPEC-049): when true, `apiFetch` does NOT stringify `body` and
   * does NOT set `Content-Type: application/json`. Use this for FormData
   * bodies (avatar upload, multipart KYC docs) — the browser sets the
   * multipart boundary automatically.
   */
  skipJsonStringify?: boolean;
  /**
   * P-273 (SPEC-155): when true, `apiFetch` returns the FULL response body
   * without unwrapping `.data`. Use this for endpoints that return
   * `{ data: T, pagination: P }` (or any other shape with extra metadata
   * alongside `data`) where you need access to the metadata, not just the
   * `data` field.
   *
   * Without `rawResponse`, `apiFetch` auto-unwraps `.data` (see
   * `handleResponse` L1399-1401), which is correct for the majority of
   * endpoints but breaks callers that declare the type as
   * `{ data: Foo; pagination: Bar }` and then access `res.data` /
   * `res.pagination` (which would be `undefined` because `res` is already
   * the unwrapped `Foo`, not the envelope).
   *
   * When `rawResponse: true` is passed, `apiFetch<T>` returns the full
   * body typed as `T` (caller is responsible for declaring `T` as the
   * full envelope shape).
   */
  rawResponse?: boolean;
}

/**
 * Internal helper: wrap `fetch()` with an `AbortController` that fires after
 * `timeoutMs`. Combines the internal timeout signal with an optional external
 * `AbortSignal` (so callers passing `opts.signal` still get their abort
 * honored, AND we add our own timeout on top).
 *
 * Returns the Response on success. On failure, throws:
 *   - `ApiClientError(code='TIMEOUT', status=408)` when OUR timeout fires.
 *   - The original `DOMException` (name='AbortError') when the EXTERNAL
 *     signal aborts (so callers checking for `AbortError` still work —
 *     e.g., `admin/dashboard/page.tsx` silently bails on unmount abort).
 *   - `ApiClientError(code='NETWORK_ERROR', status=0)` for genuine network
 *     failures (DNS, connection refused, offline, CORS).
 *
 * Anti-§6 (CM-19/CM-15): no business logic — pure transport hardening.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
  externalSignal?: AbortSignal | null,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = timeoutMs > 0
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : null;

  // Link external signal to our controller so an external abort propagates.
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (networkErr) {
    // BUG-EXP5-003: distinguish our internal timeout from external abort
    // and from real network failures.
    if (timedOut) {
      throw new ApiClientError(
        'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.',
        'TIMEOUT',
        408,
      );
    }
    // External signal abort — re-throw the original DOMException so callers
    // can detect `AbortError` and silently bail (admin/dashboard pattern).
    if (externalSignal?.aborted) {
      throw networkErr;
    }
    throw new ApiClientError(
      'No se pudo conectar con el servidor. Verifica tu conexión e intenta de nuevo.',
      'NETWORK_ERROR',
      0, // status 0 = network failure (retryable)
    );
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { auth = true, skipRefresh = false, headers, signal: externalSignal, timeoutMs, skipJsonStringify = false, rawResponse = false, ...rest } = opts;
  // 4F-LOW-009 (SPEC-047 R3-BATCH-6): defensive warning for one-shot body
  // types that are NOT retry-safe across the 401→refresh→retry path below.
  // The browser's `fetch()` reads the body stream once; for bodies that are
  // consumed destructively (Blob, ArrayBuffer, TypedArray, ReadableStream),
  // the second `fetch()` call would fail with a TypeError (`body has
  // already been used`). The previous JSDoc on `FetchOptions.body` (see
  // AUDIT3-SHARED-008 comment block above `interface FetchOptions`)
  // documented this constraint but did NOT enforce it — callers passing a
  // one-shot body type would silently work for the first fetch (no 401)
  // and break loudly on the 401-retry path (a rare condition easy to miss
  // in dev). The console.warn here makes the issue visible in the browser
  // console the FIRST time the call is made (not just on retry) so the
  // caller sees it during dev/testing instead of debugging a production
  // TypeError. Callers that MUST send a one-shot body type should set
  // `skipRefresh: true` so the 401-retry path is bypassed entirely.
  if (
    !skipRefresh &&
    rest.body !== undefined &&
    rest.body !== null &&
    (rest.body instanceof Blob ||
      rest.body instanceof ArrayBuffer ||
      (typeof ReadableStream !== 'undefined' && rest.body instanceof ReadableStream) ||
      ArrayBuffer.isView(rest.body))
  ) {
    console.warn(
      'apiFetch: body type is not retry-safe — if the request hits 401, the ' +
      'transparent refresh+retry path will fail with `TypeError: body has ' +
      'already been used`. Pass `skipRefresh: true` to bypass the retry ' +
      'path (and consume the body exactly once). See AUDIT3-SHARED-008 ' +
      'in `interface FetchOptions` JSDoc for safe body types.',
    );
  }
  // Bug 2 (SPEC-049): pick the timeout class based on HTTP method + body
  // type. GETs are short (8s), POST/PUT/DELETE without FormData are
  // medium (15s), FormData uploads get the long timeout (30s). Callers
  // passing `opts.timeoutMs` explicitly override this — backward compat.
  // with the previous single `DEFAULT_TIMEOUT_MS` (30s) value.
  const method = (rest.method ?? 'GET').toUpperCase();
  const isFormDataBody = typeof FormData !== 'undefined' && rest.body instanceof FormData;
  const methodAwareTimeout = isFormDataBody
    ? UPLOAD_TIMEOUT_MS
    : method === 'GET' || method === 'HEAD'
      ? READ_TIMEOUT_MS
      : WRITE_TIMEOUT_MS;
  const finalHeaders: Record<string, string> = {
    // Bug 6 (SPEC-049): when `skipJsonStringify` is set (FormData upload),
    // do NOT set `Content-Type: application/json` — the browser sets the
    // multipart boundary automatically when no Content-Type is provided.
    ...(skipJsonStringify ? {} : { 'Content-Type': 'application/json' }),
    ...(headers as Record<string, string>),
  };
  if (auth) {
    const access = tokenStore.getAccess();
    if (access) finalHeaders.Authorization = `Bearer ${access}`;
  }

  const url = `${API_PREFIX}${path}`;
  const resolvedTimeout = timeoutMs ?? methodAwareTimeout;
  // Bug 6 (SPEC-049): `skipJsonStringify` only controls the `Content-Type`
  // header — callers ALWAYS pass an already-stringified body (JSON) OR a
  // FormData / URLSearchParams / Blob body. The pre-Bug-6 code never
  // stringified bodies itself (callers did via `body: JSON.stringify(input)`),
  // so we don't need to stringify here either. The flag's role is to OMIT
  // the `Content-Type: application/json` header so the browser sets the
  // multipart boundary automatically for FormData bodies.
  // BUG-FE-016 fix de raíz (SPEC-036): wrap fetch in try/catch so that
  // network-level failures (DNS error, connection refused, offline, CORS)
  // are converted into ApiClientError with a friendly message and a
  // status of 0 (retryable). Without this, callers see a raw TypeError
  // which they cannot distinguish from a programming error.
  // BUG-EXP5-003: fetchWithTimeout also enforces a default 30s timeout
  // (overridable via opts.timeoutMs) so a hung backend surfaces as a
  // clear TIMEOUT error instead of an infinite loading state.
  let res: Response;
  try {
    res = await fetchWithTimeout(
      url,
      { ...rest, headers: finalHeaders, credentials: 'include' },
      resolvedTimeout,
      externalSignal,
    );
  } catch (networkErr) {
    // fetchWithTimeout already wraps in ApiClientError for TIMEOUT and
    // NETWORK_ERROR. The only non-ApiClientError case is an external
    // signal abort (DOMException AbortError) — propagate as-is so callers
    // can detect AbortError and silently bail.
    if (networkErr instanceof ApiClientError) throw networkErr;
    throw networkErr;
  }

  // Si 401 y no estamos ya en refresh, intentar refresh transparente
  if (res.status === 401 && auth && !skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      // Reintentar con nuevo token
      const newAccess = tokenStore.getAccess();
      if (newAccess) finalHeaders.Authorization = `Bearer ${newAccess}`;
      const retry = await fetchWithTimeout(
        url,
        { ...rest, headers: finalHeaders, credentials: 'include' },
        resolvedTimeout,
        externalSignal,
      );
      // P-273 (SPEC-155): pass rawResponse so handleResponse knows whether
      // to unwrap `.data` from the response envelope or return the full body.
      return handleResponse<T>(retry, rawResponse);
    }
    tokenStore.clear();
    if (typeof window !== 'undefined') {
      // M-18: Set a global flag instead of hard-redirecting.
      // AppShell or other components can detect this and show a
      // session-expired toast/modal with a "Log in again" button.
      // M-18 fix (R2 SPEC-037): Use typed Window.__profileia from global.d.ts
      if (!window.__profileia) window.__profileia = { access: null };
      window.__profileia.sessionExpired = true;
      // A26 (SPEC-038 Wave 3 FE-1): dispatch a CustomEvent so the admin
      // layout (and any other subscriber) can react immediately by
      // redirecting to /login?reason=session_expired, instead of waiting
      // up to 30s for the next poll.
      window.dispatchEvent(new CustomEvent('profileia:session-expired'));
    }
    // BUG-EXP5-007: preserve the backend's `code` from the 401 response
    // body (could be AUTH_TOKEN_INVALID, AUTH_TOKEN_REVOKED,
    // AUTH_SESSION_NOT_FOUND, etc.) instead of always hardcoding
    // AUTH_TOKEN_EXPIRED. Falls back to AUTH_TOKEN_EXPIRED if the body
    // can't be parsed or has no `code` field.
    let realCode = 'AUTH_TOKEN_EXPIRED';
    try {
      const cloned = res.clone();
      const body = await cloned.json() as { code?: string };
      if (body && typeof body.code === 'string' && body.code.length > 0) {
        realCode = body.code;
      }
    } catch {
      // Body wasn't JSON or couldn't be parsed — keep the default.
    }
    throw new ApiClientError('Sesión expirada', realCode, 401);
  }

  // P-273 (SPEC-155): pass rawResponse so handleResponse knows whether
  // to unwrap `.data` from the response envelope or return the full body.
  return handleResponse<T>(res, rawResponse);
}

async function handleResponse<T>(res: Response, rawResponse: boolean = false): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  // A27 (SPEC-038 Wave 3 FE-1): wrap JSON.parse in try/catch. The body
  // may be empty or contain non-JSON content (e.g., an HTML error page
  // from a reverse proxy, a 502 from a stale upstream, or a truncated
  // response). Previously a malformed body threw an unhandled SyntaxError
  // that crashed the calling code with no ApiClientError context.
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      if (!res.ok) {
        // Surface the raw text (truncated) so callers see what came back
        // instead of a generic "HTTP 502" with no body context.
        const snippet = text.length > 200 ? `${text.slice(0, 200)}…` : text;
        throw new ApiClientError(
          `HTTP ${res.status}: respuesta no JSON`,
          'INVALID_JSON',
          res.status,
          { raw: snippet },
        );
      }
      // 2xx with non-JSON body (rare — e.g., a misconfigured proxy that
      // strips Content-Type). D-017 fix (SPEC-043 Batch D): throw an
      // ApiClientError('INVALID_JSON') instead of returning `{} as T`.
      // Pre-fix, the cast `{} as T` lied to TypeScript — callers
      // expecting `T` with required fields got `{}` and crashed on
      // property access. Now callers see a clear error and can handle
      // it gracefully (the same way they'd handle any other
      // ApiClientError). The 204 case above (`return undefined as T`)
      // is kept as-is — callers SHOULD use `apiFetch<void>` or
      // `apiFetch<undefined>` for endpoints that return 204.
      const snippet = text.length > 200 ? `${text.slice(0, 200)}…` : text;
      throw new ApiClientError(
        `HTTP ${res.status}: respuesta no JSON`,
        'INVALID_JSON',
        res.status,
        { raw: snippet },
      );
    }
  }
  if (!res.ok) {
    const err = (body ?? {}) as Partial<ApiError>;
    // BUG-FE-016 fix de raíz (SPEC-036): improve 5xx error UX. Instead of
    // surfacing the raw HTTP status code to the user (which the previous
    // `err.error ?? \`HTTP ${res.status}\`` fallback did for 5xx responses
    // without a JSON body), translate well-known 5xx codes into actionable
    // user-facing messages. The caller can then surface a toast with a
    // "Reintentar" CTA. Spec ref: SPEC-NEGOCIO §9.6 INV-SEC-06 (graceful
    // degradation), GOBERNANZA-QA CM-08 (fix de raíz — centralize here so
    // every apiFetch call benefits, no per-call try/catch needed).
    if (res.status >= 500) {
      const friendly = SERVER_ERROR_MESSAGES[res.status] ?? 'Error del servidor. Intenta de nuevo en unos momentos.';
      throw new ApiClientError(
        err.error ?? friendly,
        err.code ?? 'SERVER_ERROR',
        res.status,
        err.details,
      );
    }
    // INT-006 fix (Bloque E): when ANY API call returns 403 with
    // `code:'2FA_REQUIRED'`, dispatch a global window event so the admin
    // layout's 2FA re-verify modal opens. The user verifies 2FA, then
    // re-fires the action (or the layout dispatches a
    // `profileia:admin-2fa-verified` event listeners can use to retry).
    // Anti-§6: NO new business logic — only the UI hook for the existing
    // `requireRecent2FA`/`requireAdmin` 403 response shape standardized
    // in Bloque E (INT-006).
    if (
      res.status === 403 &&
      err.code === '2FA_REQUIRED' &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new CustomEvent('profileia:admin-2fa-required'));
    }
    throw new ApiClientError(
      err.error ?? `HTTP ${res.status}`,
      err.code ?? 'UNKNOWN',
      res.status,
      err.details,
    );
  }
  // La API devuelve { data: ... } para éxito
  // P-273 (SPEC-155): when rawResponse=true, return the FULL body without
  // unwrapping `.data`. This is needed for endpoints that return
  // `{ data, pagination }` (or any other shape with extra metadata
  // alongside `data`) where the caller needs access to the metadata.
  // Without rawResponse, apiFetch auto-unwraps `.data` (the historical
  // behavior since SPEC-038 — most endpoints just need the `data` field).
  if (!rawResponse && body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

// ─── Sprint 18 Phase 4 — Response validation helper ─────────────────────────
//
// `apiFetchValidated` wraps `apiFetch` and runs a Zod schema against the
// response data. Use this for endpoints where a shape mismatch would cause
// silent UI bugs (financial data, wallet-connect flow, etc.). The schema
// is permissive on extra fields (no `.strict()`) — the goal is to catch
// MISSING required fields + WRONG TYPES, not to enforce schema parity.
//
// Anti-§6 (CM-19/CM-15): the schema is the SAME one the backend uses to
// validate request bodies (where applicable) or a dedicated response schema
// from `@profileia/shared/schemas/api-responses`. Single source of truth.
//
// If validation fails, a ZodError is thrown with a clear `issues` array
// pointing to the exact field that mismatched. The caller can catch this
// and surface a "backend response shape mismatch" error to the admin
// (instead of silently rendering undefined values).
//
// SPEC-105 Task D.3 (P-167): the param type is now the STRUCTURAL
// `ValidatableSchema<T>` (anything with `parse(data: unknown): T`) instead
// of `ZodType<T>`. Two reasons, both documented in the PR:
//   1. Tipado directo: `accountingSummaryResponseSchema` (from
//      @profileia/shared) must be passable WITHOUT the historic cast
//      `as unknown as ZodType<AccountingSummary>` — that cast was the mask
//      that let the DTO↔schema drift survive tsc (P-167 root cause
//      accomplice). The compile-time drift gate now lives in
//      `packages/shared/src/schemas/api-responses.parity.ts` (Task D.1).
//   2. Divergencia vs premisa del SPEC (documentada): el repo tiene un
//      SPLIT de versiones de zod — `packages/shared` resuelve zod 3.25
//      (root node_modules) mientras que `frontend/node_modules/zod` es
//      4.4.3 (dependencia transitiva hoisted). Un ZodType zod4 NO es
//      estructuralmente compatible con los schemas zod3 de shared — el
//      cast también puentaba ese split de tipos. El contrato ESTRUCTURAL
//      (solo `parse`) es la única firma honesta para ambos: es TODO lo que
//      esta función usa del schema, y acepta zod3 y zod4 por igual.
export interface ValidatableSchema<T> {
  parse(data: unknown): T;
}

export async function apiFetchValidated<T>(
  path: string,
  schema: ValidatableSchema<T>,
  opts: FetchOptions = {},
): Promise<T> {
  const data = await apiFetch<unknown>(path, opts);
  const parsed = schema.parse(data);
  // P-167 fix (SPEC-105 Task B — QA SITE, decisión Founder Q2 = Consola):
  // detector de amputación. `z.object()` default STRIP-mode NO falla ante
  // campos extra del backend — los ELIMINA silenciosamente del objeto
  // parseado (exactamente lo que volvió invisible la sección "Balances de
  // Usuarios" de SPEC-104). Aquí comparamos las claves top-level del input
  // contra las del output: las presentes en el input y ausentes en el
  // output fueron amputadas → console.error estructurado para que el drift
  // de contrato sea VISIBLE en la consola del navegador (el smoke §6.1 de
  // "consola limpia" lo detecta en la próxima corrida).
  //
  // SIN throw (fail-safe, decisión Q2): el render sigue siendo el mismo de
  // hoy — el campo amputado ya es undefined; el objetivo del detector es
  // la señal de diagnóstico, no romper la UX.
  //
  // Solo objetos planos no-nulos (arrays top-level no pueden ser amputados
  // a nivel raíz — sus claves son índices idénticos input/output).
  if (
    data !== null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    parsed !== null &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed)
  ) {
    const outputKeys = new Set(Object.keys(parsed));
    const stripped = Object.keys(data).filter((key) => !outputKeys.has(key));
    if (stripped.length > 0) {
      console.error(
        `[api-schema-strip] ${(opts.method ?? 'GET').toString().toUpperCase()} ${API_PREFIX}${path} — ` +
          `el schema Zod eliminó claves del payload del backend: ${stripped.join(', ')}. ` +
          `Actualizar packages/shared/src/schemas/api-responses.ts.`,
      );
    }
  }
  return parsed;
}

// ─── FE-004 fix: FormData-aware fetch with 401 refresh handling ──────────────
//
// `apiFetch` always sets `Content-Type: application/json`, which breaks
// multipart/form-data uploads (the browser must set the boundary itself).
// This variant mirrors the full `apiFetch` flow (auth header, 401 → tryRefresh
// → retry, network error wrapping, `handleResponse` envelope unwrap) but
// does NOT set Content-Type, allowing FormData to work correctly.
export async function apiFetchFormData<T>(
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  const { auth = true, skipRefresh = false, headers, signal: externalSignal, timeoutMs, rawResponse = false, ...rest } = opts;
  // Do NOT set Content-Type — let the browser set it with the correct
  // multipart boundary when the body is FormData.
  const finalHeaders: Record<string, string> = {
    ...(headers as Record<string, string>),
  };
  if (auth) {
    const access = tokenStore.getAccess();
    if (access) finalHeaders.Authorization = `Bearer ${access}`;
  }

  const url = `${API_PREFIX}${path}`;
  // Sprint 139 fix (2026-09-23): use UPLOAD_TIMEOUT_MS (120s) instead of
  // DEFAULT_TIMEOUT_MS (30s) — FormData uploads need more time for large
  // files on slow connections. Pre-fix, apiFetchFormData used the default
  // 30s which was too short for 3 × 5MB image uploads → "La solicitud
  // tardó demasiado" timeout error.
  const resolvedTimeout = timeoutMs ?? UPLOAD_TIMEOUT_MS;
  let res: Response;
  try {
    res = await fetchWithTimeout(
      url,
      { ...rest, headers: finalHeaders, credentials: 'include' },
      resolvedTimeout,
      externalSignal,
    );
  } catch (networkErr) {
    if (networkErr instanceof ApiClientError) throw networkErr;
    throw networkErr;
  }

  // If 401 and not already in refresh, try transparent refresh (same logic as apiFetch)
  if (res.status === 401 && auth && !skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const newAccess = tokenStore.getAccess();
      if (newAccess) finalHeaders.Authorization = `Bearer ${newAccess}`;
      const retry = await fetchWithTimeout(
        url,
        { ...rest, headers: finalHeaders, credentials: 'include' },
        resolvedTimeout,
        externalSignal,
      );
      return handleResponse<T>(retry, rawResponse);
    }
    tokenStore.clear();
    if (typeof window !== 'undefined') {
      if (!window.__profileia) window.__profileia = { access: null };
      window.__profileia.sessionExpired = true;
      window.dispatchEvent(new CustomEvent('profileia:session-expired'));
    }
    // BUG-EXP5-007: preserve the backend's `code` from the 401 response body.
    let realCode = 'AUTH_TOKEN_EXPIRED';
    try {
      const cloned = res.clone();
      const body = await cloned.json() as { code?: string };
      if (body && typeof body.code === 'string' && body.code.length > 0) {
        realCode = body.code;
      }
    } catch {
      // Body wasn't JSON — keep the default.
    }
    throw new ApiClientError('Sesión expirada', realCode, 401);
  }

  return handleResponse<T>(res, rawResponse);
}

// BUG-FE-016: User-friendly messages for 5xx server errors.
let refreshPromise: Promise<boolean> | null = null;

// R2-UI-004 fix: cooldown timestamp until which refresh attempts are blocked.
// When refresh returns 429 (rate limit) or 5xx, we set this to Date.now() +
// 60_000 so subsequent tryRefresh() calls short-circuit to false instead of
// making another request that will also fail with 429. This prevents the
// refresh loop where multiple parallel 401 responses all trigger tryRefresh()
// and exhaust the 5/min rate limit, making ALL subsequent refreshes fail
// for the next 60s. See docs/LIBRO-PRUEBAS-STAGING.md §4 R2-UI-004.
let refreshCooldownUntil = 0;

export async function tryRefresh(): Promise<boolean> {
  // R2-UI-004 fix: short-circuit if we recently got a 429/5xx — trying again
  // within the cooldown window would just make another failing request.
  if (Date.now() < refreshCooldownUntil) return false;
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      // El refresh token va en la cookie automaticamente (credentials: 'include').
      // No enviamos body — el backend lee de la cookie.
      //
      // D-001 fix (SPEC-043 Batch D): previously this used raw `fetch()`
      // with no AbortController and no timeout. If the backend hung (e.g.,
      // TCP connection accepted but no response), `refreshPromise` would
      // never settle, blocking every subsequent `apiFetch` that hit 401
      // (they all await the same `refreshPromise`) — UI stuck in infinite
      // loading. Now we wrap with `fetchWithTimeout` (10s) so a hung
      // refresh surfaces as `ApiClientError(code='TIMEOUT', status=408)`,
      // `tryRefresh` returns false, and `refreshPromise` is cleared in
      // the `finally` block (so subsequent calls can attempt a fresh
      // refresh instead of awaiting the hung one forever).
      // 10s (not 30s default) because the refresh endpoint is fast (no
      // DB write beyond rotating the refresh cookie) and users waiting
      // on auth refresh should not wait the full default window.
      const REFRESH_TIMEOUT_MS = 10_000;
      const res = await fetchWithTimeout(
        `${API_PREFIX}/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
        REFRESH_TIMEOUT_MS,
        null,
      );
      if (!res.ok) {
        // R2-UI-004 fix: on 429 (rate limit) or 5xx (server error), set a
        // 60s cooldown so subsequent tryRefresh() calls short-circuit instead
        // of cascading into more failing requests. Pages that make multiple
        // parallel API calls (/app/network does /api/auth/me + /api/network +
        // /api/kyc/status + /api/conferences) will all hit 401 when the
        // access token expires, and without this cooldown each would trigger
        // another refresh — exhausting the 5/min rate limit immediately.
        // The cooldown prevents the cascade; the user sees a graceful
        // redirect to /login instead of a console full of 429 errors.
        if (res.status === 429 || res.status >= 500) {
          refreshCooldownUntil = Date.now() + 60_000;
        }
        return false;
      }
      const body = await res.json();
      // El backend setea nueva cookie de refresh; el access token viene en body.
      // AUDIT3-SHARED-009 + 4F-LOW-002 (R3-BATCH-6): defensive dual-envelope
      // unwrap. `body.data ?? body` falls back to `body` when `body.data` is
      // `null` OR `undefined` (nullish coalescing) — so `{data: null}` (200-
      // with-error edge case) no longer throws TypeError on the subsequent
      // `data.tokens.accessToken` access. The `responseBody?.tokens?.accessToken`
      // guard short-circuits to `return false` when the unwrapped value lacks
      // a `tokens.accessToken` path (avoids `tokenStore.setAccess(undefined)`
      // + avoids the TypeError entirely).
      const responseBody = body && typeof body === 'object' ? (body.data ?? body) : null;
      if (!responseBody?.tokens?.accessToken) return false;
      tokenStore.setAccess(responseBody.tokens.accessToken);
      // H-10 fix: notify sockets (use-socket, useSpectatorRelay) that the
      // access token has been refreshed so they can reconnect with the
      // new JWT instead of continuing with the stale one.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('token-refreshed'));
        // PR #1586 (CRITICAL fix): clear `sessionExpired` flag on successful
        // refresh. Without this, the flag stays `true` after the FIRST
        // refresh failure (e.g., a 429 from a parallel refresh cascade),
        // and EVERY subsequent AppShell mount (including the one right
        // after this successful refresh) sees `sessionExpired === true`
        // and fires `router.replace('/login?reason=session_expired')` →
        // infinite redirect loop /app/dashboard ↔ /login.
        //
        // Scenario this fixes:
        //   1. User on /app/dashboard, access token expires
        //   2. Multiple parallel fetches get 401 (notifications + features +
        //      direct-messages + dashboard + auth/me = 5+ calls)
        //   3. First 401 → tryRefresh() → refresh IN-PROGRESS, all other
        //      401s await the same refreshPromise (dedup)
        //   4. Backend returns 429 on refresh (rare, but possible if the
        //      user has been on the page for >5 min and refresh cookie
        //      rotated mid-flight) OR returns 5xx
        //   5. tryRefresh returns false → apiFetch clears tokenStore +
        //      sets `sessionExpired = true` + dispatches event
        //   6. useAppShell handler fires → router.replace('/login')
        //   7. /login mounts → tokenStore empty → stays on /login
        //   8. User re-logs in → tokenStore has new token → /login
        //      useEffect redirects to /app/dashboard
        //   9. AppShell mounts → checks `window.__profileia.sessionExpired`
        //      → STILL TRUE → fires handler → redirect to /login
        //      → LOOP (screen flickers between /app/dashboard and /login)
        //
        // Fix: when refresh SUCCEEDS (here), clear the flag so step 9
        // doesn't redirect. The flag was set by a PRIOR refresh failure
        // and is now stale — the user has a valid access token again.
        if (window.__profileia) {
          window.__profileia.sessionExpired = false;
        }
      }
      return true;
    } catch {
      // D-001: fetchWithTimeout throws ApiClientError(TIMEOUT/NETWORK_ERROR)
      // or DOMException(external abort). All surface as `return false` so
      // apiFetch clears the access token + dispatches session-expired event.
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

// SPEC-171 (Sprint 85, 2026-09-10): Internal reservation system types.
// PENDIENTE-FOUNDER RESUELTO 2026-09-10: SPEC-NEGOCIO §12 (Sistema de Reservas) — ver docs/SPEC-NEGOCIO-LOGICA-VIVA.md §12.
